import React from 'react';
import {
  Briefcase,
  Search,
  Filter,
  DollarSign,
  Clock,
  Users,
  Star,
  MapPin,
  CheckCircle2,
  Send,
  FileText,
  MessageSquare,
  Award,
  TrendingUp,
  Sparkles,
  ChevronRight,
  Eye,
  Heart,
  Bookmark,
  MoreHorizontal,
  Calendar,
  Zap,
  Target,
  BadgeCheck,
  CircleDot,
  ArrowUpRight,
  Globe,
  GraduationCap,
  Beaker,
  BookOpen,
  BarChart3,
  Wallet,
  Shield,
  Trophy,
  Flame,
  PenTool,
} from 'lucide-react';
import './FreelanceMockupContent.css';

const FreelanceMockupContent = ({ visibleItems, animationPhase, activeInteriorTab, onTabClick = () => {} }) => {
  // Avatar colors
  const avatarColors = [
    'linear-gradient(135deg, #6366f1 0%, #0284c7 100%)',
    'linear-gradient(135deg, #06b6d4 0%, #10b981 100%)',
    'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
    'linear-gradient(135deg, #ec4899 0%, #0284c7 100%)',
    'linear-gradient(135deg, #10b981 0%, #06b6d4 100%)',
    'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
    'linear-gradient(135deg, #f97316 0%, #f59e0b 100%)',
    'linear-gradient(135deg, #0284c7 0%, #ec4899 100%)',
  ];

  // Featured Tasks/Gigs
  const featuredTasks = [
    {
      id: 1,
      title: 'Statistical Analysis for Clinical Trial Data',
      description: 'Need experienced statistician for Phase III trial data analysis. Must have expertise in survival analysis and SAS/R.',
      poster: { name: 'Dr. Lena Hoffmann', initials: 'LH', institution: 'Stanford Medical', rating: 4.9, verified: true },
      budget: '$800 - $1,200',
      budgetType: 'Fixed Price',
      deadline: '15 days left',
      skills: ['R', 'SAS', 'Survival Analysis', 'Clinical Trials'],
      applicants: 12,
      category: 'Data Analysis',
      urgent: true,
      featured: true,
    },
    {
      id: 2,
      title: 'Machine Learning Model for Protein Structure Prediction',
      description: 'Looking for ML expert to develop deep learning model for protein folding prediction using AlphaFold-like architecture.',
      poster: { name: 'Prof. Idris Malik', initials: 'IM', institution: 'MIT Biology', rating: 4.8, verified: true },
      budget: '$2,500 - $4,000',
      budgetType: 'Fixed Price',
      deadline: '30 days left',
      skills: ['PyTorch', 'TensorFlow', 'Bioinformatics', 'Deep Learning'],
      applicants: 8,
      category: 'Machine Learning',
      featured: true,
    },
    {
      id: 3,
      title: 'Literature Review: AI in Drug Discovery',
      description: 'Comprehensive systematic review of AI/ML applications in pharmaceutical drug discovery over the past 5 years.',
      poster: { name: 'Dr. Clara Whitfield', initials: 'CW', institution: 'Oxford Pharma', rating: 4.7, verified: false },
      budget: '$500 - $750',
      budgetType: 'Fixed Price',
      deadline: '21 days left',
      skills: ['Literature Review', 'Drug Discovery', 'AI/ML', 'Academic Writing'],
      applicants: 23,
      category: 'Research Writing',
    },
    {
      id: 4,
      title: 'Data Visualization Dashboard for Climate Research',
      description: 'Create interactive dashboard using D3.js or Plotly to visualize 20 years of climate data with multiple parameters.',
      poster: { name: 'Dr. Tobias Richter', initials: 'TR', institution: 'NOAA Research', rating: 4.9, verified: true },
      budget: '$1,000 - $1,500',
      budgetType: 'Fixed Price',
      deadline: '10 days left',
      skills: ['D3.js', 'Plotly', 'Python', 'Data Visualization'],
      applicants: 15,
      category: 'Data Visualization',
      urgent: true,
    },
  ];

  // My Posted Tasks
  const myPostedTasks = [
    {
      id: 1,
      title: 'Survey Data Analysis for Psychology Study',
      status: 'active',
      applicants: 7,
      views: 234,
      budget: '$400',
      deadline: '8 days left',
      topApplicant: { name: 'Tariq Hussain', rating: 4.9, initials: 'TQ' },
    },
    {
      id: 2,
      title: 'Research Paper Editing - Neuroscience',
      status: 'in-review',
      applicants: 3,
      views: 156,
      budget: '$250',
      deadline: '5 days left',
      topApplicant: { name: 'Dr. Annika Strand', rating: 5.0, initials: 'AS' },
    },
    {
      id: 3,
      title: 'MATLAB Code Review for Signal Processing',
      status: 'completed',
      applicants: 5,
      budget: '$300',
      completedBy: { name: 'Declan O\'Brien', rating: 4.8, initials: 'DO' },
      rating: 5,
    },
  ];

  // My Applications
  const myApplications = [
    {
      id: 1,
      title: 'Biostatistics Analysis for Oncology Research',
      poster: { name: 'Dr. Brigitte Wenger', initials: 'BW', institution: 'Mayo Clinic' },
      status: 'accepted',
      appliedDate: '3 days ago',
      budget: '$1,200',
      message: 'Congratulations! Your proposal has been accepted.',
    },
    {
      id: 2,
      title: 'Python Script for Genome Sequencing Pipeline',
      poster: { name: 'Prof. Liu Changming', initials: 'LC', institution: 'Harvard Genetics' },
      status: 'pending',
      appliedDate: '1 day ago',
      budget: '$800',
      proposal: 'Proposed timeline: 2 weeks',
    },
    {
      id: 3,
      title: 'Machine Learning for Medical Imaging',
      poster: { name: 'Dr. Beatrice Khoury', initials: 'BK', institution: 'Johns Hopkins' },
      status: 'shortlisted',
      appliedDate: '5 days ago',
      budget: '$2,000',
      note: 'You are in the top 3 candidates!',
    },
  ];

  // Top Freelancers
  const topFreelancers = [
    {
      id: 1,
      name: 'Dr. Zara Bukhari',
      initials: 'ZB',
      title: 'Data Scientist & Statistician',
      institution: 'Stanford University',
      rating: 4.98,
      reviews: 127,
      completedJobs: 89,
      hourlyRate: '$85/hr',
      skills: ['R', 'Python', 'Statistics', 'Machine Learning'],
      available: true,
      topRated: true,
      earnings: '$125K+',
    },
    {
      id: 2,
      name: 'Prof. Henrik Müller',
      initials: 'HM',
      title: 'Research Writer & Editor',
      institution: 'Yale University',
      rating: 4.95,
      reviews: 203,
      completedJobs: 156,
      hourlyRate: '$65/hr',
      skills: ['Academic Writing', 'Editing', 'Grant Writing'],
      available: true,
      earnings: '$95K+',
    },
    {
      id: 3,
      name: 'Dr. Mei-Lin Chou',
      initials: 'MC',
      title: 'Bioinformatics Expert',
      institution: 'MIT',
      rating: 4.92,
      reviews: 84,
      completedJobs: 62,
      hourlyRate: '$95/hr',
      skills: ['Bioinformatics', 'Genomics', 'Python', 'R'],
      available: false,
      topRated: true,
      earnings: '$78K+',
    },
    {
      id: 4,
      name: 'Dr. Rafiq Chaudhry',
      initials: 'RC',
      title: 'ML & Deep Learning Specialist',
      institution: 'Berkeley AI Lab',
      rating: 4.89,
      reviews: 156,
      completedJobs: 98,
      hourlyRate: '$110/hr',
      skills: ['PyTorch', 'TensorFlow', 'Computer Vision', 'NLP'],
      available: true,
      earnings: '$145K+',
    },
  ];

  // My Earnings/Stats
  const earningsData = {
    totalEarnings: '$12,450',
    thisMonth: '$2,840',
    pendingPayments: '$1,200',
    completedProjects: 24,
    activeProjects: 3,
    avgRating: 4.92,
    totalReviews: 31,
    successRate: '98%',
    recentPayments: [
      { project: 'Data Analysis - Clinical Trial', amount: '$950', date: 'Jan 28', status: 'completed' },
      { project: 'Python Automation Script', amount: '$450', date: 'Jan 25', status: 'completed' },
      { project: 'Literature Review - AI', amount: '$680', date: 'Jan 20', status: 'completed' },
    ],
  };

  // Categories
  const categories = [
    { name: 'Data Analysis', count: 234, icon: BarChart3, color: 'blue' },
    { name: 'Machine Learning', count: 189, icon: Sparkles, color: 'teal' },
    { name: 'Research Writing', count: 312, icon: PenTool, color: 'green' },
    { name: 'Statistics', count: 156, icon: TrendingUp, color: 'amber' },
    { name: 'Bioinformatics', count: 98, icon: Beaker, color: 'cyan' },
    { name: 'Visualization', count: 145, icon: Eye, color: 'pink' },
  ];

  // Tabs
  const tabs = [
    { id: 'browse', label: 'Browse Tasks', icon: Search },
    { id: 'my-tasks', label: 'My Tasks', icon: Briefcase },
    { id: 'applications', label: 'Applications', icon: Send },
    { id: 'freelancers', label: 'Top Experts', icon: Users },
    { id: 'earnings', label: 'Earnings', icon: Wallet },
  ];

  // Render Browse Tasks Tab
  const renderBrowseTab = () => (
    <div className="freelance-browse">
      {/* Search Bar */}
      <div className={`freelance-search-section ${visibleItems >= 1 ? 'visible' : ''}`}>
        <div className="freelance-search-bar">
          <Search size={12} />
          <input type="text" placeholder="Search research tasks..." />
          <button className="freelance-filter-btn">
            <Filter size={10} />
            Filters
          </button>
        </div>
        <div className="freelance-quick-filters">
          <button className="freelance-quick-filter active">All Tasks</button>
          <button className="freelance-quick-filter">
            <Zap size={9} />
            Urgent
          </button>
          <button className="freelance-quick-filter">
            <Star size={9} />
            Featured
          </button>
          <button className="freelance-quick-filter">
            <DollarSign size={9} />
            High Budget
          </button>
        </div>
      </div>

      {/* Categories */}
      <div className={`freelance-categories ${visibleItems >= 2 ? 'visible' : ''}`}>
        <div className="freelance-section-header">
          <h3 className="freelance-section-title">
            <Globe size={11} />
            Browse by Category
          </h3>
        </div>
        <div className="freelance-categories-grid">
          {categories.map((cat, index) => (
            <div key={cat.name} className={`freelance-category-card freelance-category-card--${cat.color}`}>
              <cat.icon size={14} />
              <span className="freelance-category-name">{cat.name}</span>
              <span className="freelance-category-count">{cat.count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Featured Tasks */}
      <div className={`freelance-tasks-section ${visibleItems >= 3 ? 'visible' : ''}`}>
        <div className="freelance-section-header">
          <h3 className="freelance-section-title">
            <Sparkles size={11} />
            Featured Tasks
          </h3>
          <button className="freelance-show-all">
            View All <ChevronRight size={10} />
          </button>
        </div>
        <div className="freelance-tasks-list">
          {featuredTasks.slice(0, 2).map((task, index) => (
            <div 
              key={task.id} 
              className={`freelance-task-card ${visibleItems >= index + 3 ? 'visible' : ''}`}
              style={{ transitionDelay: `${index * 0.1}s` }}
            >
              <div className="freelance-task-header">
                <div className="freelance-task-badges">
                  {task.featured && (
                    <span className="freelance-badge freelance-badge--featured">
                      <Star size={8} /> Featured
                    </span>
                  )}
                  {task.urgent && (
                    <span className="freelance-badge freelance-badge--urgent">
                      <Zap size={8} /> Urgent
                    </span>
                  )}
                  <span className="freelance-badge freelance-badge--category">{task.category}</span>
                </div>
                <button className="freelance-task-bookmark">
                  <Bookmark size={12} />
                </button>
              </div>
              <h4 className="freelance-task-title">{task.title}</h4>
              <p className="freelance-task-desc">{task.description}</p>
              <div className="freelance-task-skills">
                {task.skills.slice(0, 4).map((skill, i) => (
                  <span key={i} className="freelance-skill-tag">{skill}</span>
                ))}
              </div>
              <div className="freelance-task-meta">
                <span className="freelance-task-budget">
                  <DollarSign size={10} />
                  {task.budget}
                </span>
                <span className="freelance-task-deadline">
                  <Clock size={10} />
                  {task.deadline}
                </span>
                <span className="freelance-task-applicants">
                  <Users size={10} />
                  {task.applicants} applicants
                </span>
              </div>
              <div className="freelance-task-footer">
                <div className="freelance-task-poster">
                  <div className="freelance-poster-avatar" style={{ background: avatarColors[index] }}>
                    {task.poster.initials}
                  </div>
                  <div className="freelance-poster-info">
                    <span className="freelance-poster-name">
                      {task.poster.name}
                      {task.poster.verified && <BadgeCheck size={10} className="freelance-verified" />}
                    </span>
                    <span className="freelance-poster-inst">{task.poster.institution}</span>
                  </div>
                </div>
                <button className="freelance-apply-btn">
                  Apply Now <ArrowUpRight size={10} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Render My Tasks Tab
  const renderMyTasksTab = () => (
    <div className="freelance-my-tasks">
      <div className="freelance-my-tasks-header">
        <div className="freelance-my-tasks-tabs">
          <button className="freelance-subtab active">Posted Tasks</button>
          <button className="freelance-subtab">Drafts</button>
        </div>
        <button className="freelance-post-task-btn">
          <PenTool size={10} />
          Post New Task
        </button>
      </div>

      <div className="freelance-my-tasks-list">
        {myPostedTasks.map((task, index) => (
          <div 
            key={task.id}
            className={`freelance-my-task-card ${visibleItems >= index + 1 ? 'visible' : ''}`}
            style={{ transitionDelay: `${index * 0.1}s` }}
          >
            <div className="freelance-my-task-header">
              <h4 className="freelance-my-task-title">{task.title}</h4>
              <span className={`freelance-status-badge freelance-status-badge--${task.status}`}>
                {task.status === 'active' && <CircleDot size={8} />}
                {task.status === 'in-review' && <Eye size={8} />}
                {task.status === 'completed' && <CheckCircle2 size={8} />}
                {task.status.replace('-', ' ')}
              </span>
            </div>
            <div className="freelance-my-task-stats">
              {task.status !== 'completed' ? (
                <>
                  <span><Users size={10} /> {task.applicants} applicants</span>
                  <span><Eye size={10} /> {task.views} views</span>
                  <span><DollarSign size={10} /> {task.budget}</span>
                  <span><Clock size={10} /> {task.deadline}</span>
                </>
              ) : (
                <>
                  <span><DollarSign size={10} /> {task.budget}</span>
                  <span><Star size={10} /> Rated {task.rating}/5</span>
                </>
              )}
            </div>
            {task.topApplicant && task.status !== 'completed' && (
              <div className="freelance-top-applicant">
                <span className="freelance-top-label">
                  <Trophy size={9} /> Top Applicant:
                </span>
                <div className="freelance-applicant-preview">
                  <div className="freelance-applicant-avatar" style={{ background: avatarColors[index + 3] }}>
                    {task.topApplicant.initials}
                  </div>
                  <span>{task.topApplicant.name}</span>
                  <span className="freelance-applicant-rating">
                    <Star size={9} /> {task.topApplicant.rating}
                  </span>
                </div>
              </div>
            )}
            {task.completedBy && (
              <div className="freelance-completed-by">
                <span className="freelance-completed-label">Completed by:</span>
                <div className="freelance-applicant-preview">
                  <div className="freelance-applicant-avatar" style={{ background: avatarColors[5] }}>
                    {task.completedBy.initials}
                  </div>
                  <span>{task.completedBy.name}</span>
                  <span className="freelance-applicant-rating">
                    <Star size={9} /> {task.completedBy.rating}
                  </span>
                </div>
              </div>
            )}
            <div className="freelance-my-task-actions">
              <button className="freelance-action-btn">
                <Eye size={10} /> View Details
              </button>
              {task.status === 'active' && (
                <button className="freelance-action-btn freelance-action-btn--primary">
                  <Users size={10} /> Review Applicants
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Render Applications Tab
  const renderApplicationsTab = () => (
    <div className="freelance-applications">
      <div className="freelance-applications-header">
        <div className="freelance-applications-tabs">
          <button className="freelance-subtab active">All</button>
          <button className="freelance-subtab">Pending</button>
          <button className="freelance-subtab">Accepted</button>
          <button className="freelance-subtab">Shortlisted</button>
        </div>
        <span className="freelance-applications-count">3 active applications</span>
      </div>

      <div className="freelance-applications-list">
        {myApplications.map((app, index) => (
          <div 
            key={app.id}
            className={`freelance-application-card ${visibleItems >= index + 1 ? 'visible' : ''}`}
            style={{ transitionDelay: `${index * 0.1}s` }}
          >
            <div className="freelance-application-header">
              <span className={`freelance-app-status freelance-app-status--${app.status}`}>
                {app.status === 'accepted' && <CheckCircle2 size={9} />}
                {app.status === 'pending' && <Clock size={9} />}
                {app.status === 'shortlisted' && <Star size={9} />}
                {app.status}
              </span>
              <span className="freelance-app-date">{app.appliedDate}</span>
            </div>
            <h4 className="freelance-application-title">{app.title}</h4>
            <div className="freelance-application-poster">
              <div className="freelance-poster-avatar" style={{ background: avatarColors[index + 2] }}>
                {app.poster.initials}
              </div>
              <div className="freelance-poster-info">
                <span className="freelance-poster-name">{app.poster.name}</span>
                <span className="freelance-poster-inst">{app.poster.institution}</span>
              </div>
              <span className="freelance-app-budget">{app.budget}</span>
            </div>
            {app.message && (
              <div className="freelance-app-message freelance-app-message--success">
                <CheckCircle2 size={10} />
                {app.message}
              </div>
            )}
            {app.note && (
              <div className="freelance-app-message freelance-app-message--highlight">
                <Sparkles size={10} />
                {app.note}
              </div>
            )}
            <div className="freelance-application-actions">
              {app.status === 'accepted' && (
                <button className="freelance-action-btn freelance-action-btn--success">
                  <MessageSquare size={10} /> Message Client
                </button>
              )}
              <button className="freelance-action-btn">
                <Eye size={10} /> View Details
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Render Top Freelancers Tab
  const renderFreelancersTab = () => (
    <div className="freelance-experts">
      <div className="freelance-experts-header">
        <div className="freelance-experts-search">
          <Search size={11} />
          <input type="text" placeholder="Search experts..." />
        </div>
        <div className="freelance-experts-filters">
          <button className="freelance-filter-chip active">All</button>
          <button className="freelance-filter-chip">Available</button>
          <button className="freelance-filter-chip">Top Rated</button>
        </div>
      </div>

      <div className="freelance-experts-grid">
        {topFreelancers.map((expert, index) => (
          <div 
            key={expert.id}
            className={`freelance-expert-card ${visibleItems >= index + 1 ? 'visible' : ''}`}
            style={{ transitionDelay: `${index * 0.1}s` }}
          >
            <div className="freelance-expert-header">
              <div className="freelance-expert-avatar" style={{ background: avatarColors[index] }}>
                {expert.initials}
                {expert.available && <span className="freelance-available-dot"></span>}
              </div>
              {expert.topRated && (
                <span className="freelance-top-rated-badge">
                  <Trophy size={8} /> Top Rated
                </span>
              )}
            </div>
            <h4 className="freelance-expert-name">{expert.name}</h4>
            <p className="freelance-expert-title">{expert.title}</p>
            <p className="freelance-expert-institution">
              <GraduationCap size={9} /> {expert.institution}
            </p>
            <div className="freelance-expert-stats">
              <span className="freelance-expert-rating">
                <Star size={10} /> {expert.rating}
                <span className="freelance-review-count">({expert.reviews})</span>
              </span>
              <span className="freelance-expert-jobs">
                <CheckCircle2 size={10} /> {expert.completedJobs} jobs
              </span>
            </div>
            <div className="freelance-expert-skills">
              {expert.skills.slice(0, 3).map((skill, i) => (
                <span key={i} className="freelance-expert-skill">{skill}</span>
              ))}
            </div>
            <div className="freelance-expert-footer">
              <span className="freelance-expert-rate">{expert.hourlyRate}</span>
              <button className="freelance-hire-btn">
                {expert.available ? 'Hire Now' : 'Contact'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Render Earnings Tab
  const renderEarningsTab = () => (
    <div className="freelance-earnings">
      {/* Stats Cards */}
      <div className={`freelance-earnings-overview ${visibleItems >= 1 ? 'visible' : ''}`}>
        <div className="freelance-earnings-card freelance-earnings-card--primary">
          <div className="freelance-earnings-icon">
            <Wallet size={16} />
          </div>
          <div className="freelance-earnings-info">
            <span className="freelance-earnings-label">Total Earnings</span>
            <span className="freelance-earnings-value">{earningsData.totalEarnings}</span>
          </div>
          <TrendingUp size={14} className="freelance-trend-icon" />
        </div>
        <div className="freelance-earnings-card">
          <div className="freelance-earnings-icon freelance-earnings-icon--green">
            <DollarSign size={14} />
          </div>
          <div className="freelance-earnings-info">
            <span className="freelance-earnings-label">This Month</span>
            <span className="freelance-earnings-value">{earningsData.thisMonth}</span>
          </div>
        </div>
        <div className="freelance-earnings-card">
          <div className="freelance-earnings-icon freelance-earnings-icon--amber">
            <Clock size={14} />
          </div>
          <div className="freelance-earnings-info">
            <span className="freelance-earnings-label">Pending</span>
            <span className="freelance-earnings-value">{earningsData.pendingPayments}</span>
          </div>
        </div>
      </div>

      {/* Performance Stats */}
      <div className={`freelance-performance ${visibleItems >= 2 ? 'visible' : ''}`}>
        <h4 className="freelance-performance-title">
          <BarChart3 size={11} />
          Performance
        </h4>
        <div className="freelance-performance-grid">
          <div className="freelance-perf-stat">
            <span className="freelance-perf-value">{earningsData.completedProjects}</span>
            <span className="freelance-perf-label">Completed</span>
          </div>
          <div className="freelance-perf-stat">
            <span className="freelance-perf-value">{earningsData.activeProjects}</span>
            <span className="freelance-perf-label">Active</span>
          </div>
          <div className="freelance-perf-stat">
            <span className="freelance-perf-value">
              <Star size={10} /> {earningsData.avgRating}
            </span>
            <span className="freelance-perf-label">Avg Rating</span>
          </div>
          <div className="freelance-perf-stat">
            <span className="freelance-perf-value">{earningsData.successRate}</span>
            <span className="freelance-perf-label">Success Rate</span>
          </div>
        </div>
      </div>

      {/* Recent Payments */}
      <div className={`freelance-recent-payments ${visibleItems >= 3 ? 'visible' : ''}`}>
        <h4 className="freelance-payments-title">
          <FileText size={11} />
          Recent Payments
        </h4>
        <div className="freelance-payments-list">
          {earningsData.recentPayments.map((payment, index) => (
            <div key={index} className="freelance-payment-item">
              <div className="freelance-payment-info">
                <span className="freelance-payment-project">{payment.project}</span>
                <span className="freelance-payment-date">{payment.date}</span>
              </div>
              <div className="freelance-payment-amount">
                <span className="freelance-payment-value">{payment.amount}</span>
                <span className="freelance-payment-status">
                  <CheckCircle2 size={9} /> {payment.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Get content for active tab
  const getTabContent = () => {
    switch (activeInteriorTab) {
      case 'browse':
        return renderBrowseTab();
      case 'my-tasks':
        return renderMyTasksTab();
      case 'applications':
        return renderApplicationsTab();
      case 'freelancers':
        return renderFreelancersTab();
      case 'earnings':
        return renderEarningsTab();
      default:
        return renderBrowseTab();
    }
  };

  return (
    <div className="freelance-mockup-content">
      {/* Header */}
      <div className="freelance-mock-header">
        <div className="freelance-header-icon">
          <Briefcase size={16} />
        </div>
        <div className="freelance-header-text">
          <h2 className="freelance-header-title">Research Marketplace</h2>
          <p className="freelance-header-subtitle">Find experts or offer your research skills</p>
        </div>
        <div className="freelance-header-stats">
          <div className="freelance-header-stat">
            <span className="freelance-stat-value">2,450+</span>
            <span className="freelance-stat-label">Tasks</span>
          </div>
          <div className="freelance-header-stat">
            <span className="freelance-stat-value">8,200+</span>
            <span className="freelance-stat-label">Experts</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="freelance-main-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`freelance-main-tab ${activeInteriorTab === tab.id ? 'active' : ''}`}
            onClick={() => onTabClick(tab.id)}
          >
            <tab.icon size={11} />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="freelance-content">
        {getTabContent()}
      </div>
    </div>
  );
};

export default FreelanceMockupContent;
